import { LoadingStates } from '@pb/utils'
import { curry } from 'ramda'

import appSelectors, { getMarketCode } from 'App/duck/selectors'

import { Article } from '../components/OrderDetailsPage/StyleYourLatestCarousel/types'
import { sortByDate } from '../helpers/purchases'
import {
  PurchasesState,
  TotalOrdersCount,
  AddToBagVerificationErrorData,
  AddToBagVerificationProductData,
} from './types'

const getPurchasesState = (state): PurchasesState => state?.account?.purchases
const getSelectedTab = (state): any => getPurchasesState(state).selectedTab
const getAppState = (state): any => state.app
const getSettings = (state): any => getAppState(state).settings

// ----------------------------------------
// LOADERS
// ----------------------------------------
const getIsOnlineOrdersLoading = (state): any => getPurchasesState(state).isOnlineOrdersLoading
const getIsStoreOrdersLoading = (state): any => getPurchasesState(state).isStoreOrdersLoading

const getIsOrdersLoading = (state): any =>
  getIsOnlineOrdersLoading(state) || getIsStoreOrdersLoading(state)

const getIsOrderDetailsLoading = (state): any => getPurchasesState(state)?.isOrderDetailsLoading
const getIsOrderDetailsLoadingFailed = (state): any =>
  getPurchasesState(state)?.isOrderDetailsLoadingFailed

const getShouldShowLoader = (state): any =>
  appSelectors.getShowLoaders(state) ||
  getIsOrdersLoading(state) ||
  appSelectors.getIsUserLoading(state) ||
  appSelectors.getIsSettingsLoading(state)

// Active orders
const getOnlineActiveOrders = (state): any => getPurchasesState(state).onlineActiveOrders
const getIsOnlineActiveOrdersLoading = (state): any =>
  getPurchasesState(state).onlineActiveOrdersLoadingState === LoadingStates.Loading
const getIsOnlineActiveOrdersFailed = (state): any =>
  getPurchasesState(state).onlineActiveOrdersLoadingState === LoadingStates.LoadedFail
const getIsOnlineActiveOrdersSuccess = (state): any =>
  getPurchasesState(state).onlineActiveOrdersLoadingState === LoadingStates.LoadedSuccess
const getShouldFetchOnlineActiveOrders = (state): any =>
  getPurchasesState(state).onlineActiveOrdersLoadingState === LoadingStates.NotLoaded
const getOnlineActiveOrdersStatus = (state): boolean =>
  !!getPurchasesState(state).onlineActiveOrders.length

const getHubOrderList = (state): any => getPurchasesState(state).hubOrderList
const getFilteredHubOrderList = (state): unknown =>
  getPurchasesState(state).hubOrderList?.filter((item) => item.orderType === 'online')
const getIsOrderLoading = (state, orderId): any =>
  getHubOrderList(state).find((order) => order.orderId === orderId).isLoading

const getOnlineOrders = (state): any => getPurchasesState(state).onlineOrders
const getIsOnlineOrderLoading = (state, orderId): any =>
  getOnlineOrders(state).find((order) => order.orderId === orderId).isLoading

const getOnlineOrdersDetails = (state): any => getPurchasesState(state)?.onlineOrdersDetails
const getIsOnlineOrderLoaded = (state, orderId): any =>
  !!getOnlineOrdersDetails(state)?.find((order) => order.orderId === orderId)

const getStoreOrders = (state): any => getPurchasesState(state)?.storeOrders
const getIsStoreOrderLoading = (state, orderId): any => {
  const storeOrders = getStoreOrders(state).find((order) => order.orderId === orderId)
  return storeOrders ? storeOrders.isLoading : false
}

const getIsHubOrderListPopulated = (state): any => getPurchasesState(state).isHubOrderListPopulated
const getIsOnlineOrdersPopulated = (state): any => getPurchasesState(state).isOnlineOrdersPopulated
const getIsStoreOrdersPopulated = (state): any => getPurchasesState(state).isStoreOrdersPopulated
const getStoreOrdersDetails = (state): any => getPurchasesState(state)?.storeOrdersDetails

const getIsStoreOrderLoaded = (state, orderId): any =>
  !!getStoreOrdersDetails(state)?.find((order) => order.orderId === orderId)

const getShouldSetHubOrderList = (state): any => {
  const isOnlineOrdersLoading = getIsOnlineOrdersLoading(state)
  const isOnlineOrdersPopulated = getIsOnlineOrdersPopulated(state)
  const isStoreOrdersLoading = getIsStoreOrdersLoading(state)
  const isStoreOrdersPopulated = getIsStoreOrdersPopulated(state)

  return (
    (isOnlineOrdersPopulated && !isStoreOrdersLoading) ||
    (isStoreOrdersPopulated && !isOnlineOrdersLoading) ||
    (isOnlineOrdersPopulated && isStoreOrdersPopulated)
  )
}

const getIsOnlineOrdersLoadingFailed = (state): any =>
  getPurchasesState(state).isOnlineOrdersLoadingFailed

const getShouldFetchOnlineOrders = (state): any =>
  !getIsOnlineOrdersLoading(state) &&
  !getIsOnlineOrdersPopulated(state) &&
  !getIsOnlineOrdersLoadingFailed(state)

const getIsStoreOrdersLoadingFailed = (state): any =>
  getPurchasesState(state).isStoreOrdersLoadingFailed

const getShouldFetchStoreOrders = (state): any =>
  appSelectors.getIsDigitalReceiptEnabled(state) &&
  appSelectors.getUserIsFullMember(state) &&
  !getIsStoreOrdersLoading(state) &&
  !getIsStoreOrdersPopulated(state) &&
  !getIsStoreOrdersLoadingFailed(state)

const getNoMoreOnlineResults = (state): any => getPurchasesState(state).noMoreOnlineResults
const getShouldShowLoadMoreOnlineButton = (state): any =>
  getIsOnlineOrdersPopulated(state) && !getNoMoreOnlineResults(state)

const getNoMoreStoreResults = (state): any => getPurchasesState(state).noMoreStoreResults
const getShouldShowLoadMoreStoreButton = (state): any =>
  getIsStoreOrdersPopulated(state) && !getNoMoreStoreResults(state)

const shouldShowOnlineLoader = (state): any =>
  appSelectors.getShowLoaders(state) ||
  ((appSelectors.getIsUserLoading(state) ||
    appSelectors.getIsSettingsLoading(state) ||
    !getIsOnlineOrdersPopulated(state)) &&
    !getIsOnlineOrdersLoadingFailed(state))

const shouldShowStoreLoader = (state): any =>
  appSelectors.getShowLoaders(state) ||
  ((appSelectors.getIsUserLoading(state) ||
    appSelectors.getIsSettingsLoading(state) ||
    !getIsStoreOrdersPopulated(state)) &&
    !getIsStoreOrdersLoadingFailed(state))

const getIsOnlineOrderDetailsLoaded = (orderId, state): any =>
  !!getOnlineOrdersDetails(state)?.find((onlineOrder) => onlineOrder.orderId === orderId)

const getIsStoreOrderDetailsLoaded = (orderId, state): any =>
  !!getStoreOrdersDetails(state)?.find((storeOrder) => storeOrder.orderId === orderId)

const getIsOrdersLoadingFailed = (state): any =>
  getIsOnlineOrdersLoadingFailed(state) && getIsStoreOrdersLoadingFailed(state)

const getIsCancelOnlineOrderLoading = (state): any =>
  getPurchasesState(state).cancelOnlineOrderLoadingState === LoadingStates.Loading
const getIsCancelOnlineOrderFailed = (state): any =>
  getPurchasesState(state).cancelOnlineOrderLoadingState === LoadingStates.LoadedFail
const getIsCancelOnlineOrderSuccess = (state): any =>
  getPurchasesState(state).cancelOnlineOrderLoadingState === LoadingStates.LoadedSuccess
const getCancelOrderWhyOptions = (state): any => state?.app?.settings?.cancelOrderWhyOptions
const getIsOrderCancellationEnabled = (state): any => state?.app?.settings?.orderCancellationEnabled

const getIsOnlineReceiptEnabled = (state): any =>
  appSelectors.getHasSettingsLoaded(state) && appSelectors.getIsOnlineReceiptEnabled(state)

const getCancelAccountWhyOptions = (state): any => state?.app?.settings?.cancelAccountWhyOptions
const getShowDeleteAccountSurvey = (state): any => state?.app?.settings?.showDeleteAccountSurvey

const getShouldCallRateAndReviewAPI = (state): boolean =>
  getPurchasesState(state).shouldCallRateAndReviewAPI
// ----------------------------------------
// RESULT
// ----------------------------------------

// Gets all online and store orders, combines them sorted on date and picks the last 5
const getCombinedOrderList = (state): any => {
  const MAX_COMBINED_ORDERS = 5

  return [...getOnlineOrders(state), ...getStoreOrders(state)]
    .sort(sortByDate)
    .slice(0, MAX_COMBINED_ORDERS)
}

const getIsOrdersResultEmpty = (state): any =>
  !getIsOnlineOrdersLoading(state) &&
  !getIsStoreOrdersLoading(state) &&
  getIsHubOrderListPopulated(state) &&
  getHubOrderList(state).length === 0

const getOnlineOrderDetailsById = (state, orderId): any =>
  getOnlineOrdersDetails(state)?.find((onlineOrder) => onlineOrder.orderId === orderId)
const getIsOnlineKlarnaOrder = curry((state, orderId): any => {
  const order = getOnlineOrders(state).find((onlineOrder) => onlineOrder.orderId === orderId) || {}
  return order.paymentMode === 'Klarna'
})

const getStoreOrderDetailsById = (state, orderId): any =>
  getStoreOrdersDetails(state)?.find((storeOrder) => storeOrder.orderId === orderId)

const getTotalOrdersCount = (state): TotalOrdersCount => getPurchasesState(state)?.totalOrdersCount

// ----------------------------------------
// FETCH INDEXES
// ----------------------------------------
const getFetchOnlineIndex = (state): any => getPurchasesState(state).fetchOnlineIndex
const getFetchStoreIndex = (state): any => getPurchasesState(state).fetchStoreIndex

const getIsTrackingRun = (state): any => getPurchasesState(state).isTrackingRun

const getShouldDoTracking = (state): any =>
  appSelectors.getHasUserLoaded(state) && !getIsTrackingRun(state)

const getShouldShowTrackingId = (state): any => getMarketCode(state) === 'CN'

const getIsStoreOrderAddressesFailed = (state): any =>
  !!getPurchasesState(state).isStoreOrdersAdressesFailed

const getIsProductResellEnabled = (state): any => getSettings(state)?.productResellEnabled
const getSYLArticles = (state): Article[] => getPurchasesState(state)?.styleYourLatest?.articles

const getUserHasOrderRecommendations = (state): boolean =>
  getPurchasesState(state)?.styleYourLatest?.userHasOrderRecommendations

const getRewearInfoUrl = (state): string => getSettings(state).rewearInfoUrl

const getbackToOrderUrl = (state): string => getPurchasesState(state).backToOrderUrl as string

const getcodReturnUrl = (state): string => getPurchasesState(state).codReturnUrl as string

const getIsAddToBagLoading = (state): boolean => getPurchasesState(state).isAddtoBagLoading
const getAddToBagVerificationItem = (state): AddToBagVerificationProductData | {} =>
  getPurchasesState(state).addToBagVerificationItem
const getShowAddToBagVerification = (state): boolean =>
  getPurchasesState(state).shouldShowVerification
const getAddToBagVerificationError = (state): AddToBagVerificationErrorData =>
  getPurchasesState(state).addToBagVerificationError

export default {
  getIsOnlineOrdersLoading,
  getIsStoreOrdersLoading,
  getIsOrdersLoading,
  getIsOrderDetailsLoading,
  getIsOrderDetailsLoadingFailed,
  getShouldShowLoader,
  getIsOrderLoading,
  getIsOnlineOrderLoading,
  getIsOnlineOrderLoaded,

  getOnlineActiveOrders,
  getIsOnlineActiveOrdersLoading,
  getIsOnlineActiveOrdersFailed,
  getIsOnlineActiveOrdersSuccess,
  getOnlineActiveOrdersStatus,

  getIsStoreOrderLoading,
  getIsStoreOrderLoaded,
  getShouldSetHubOrderList,
  shouldShowOnlineLoader,
  shouldShowStoreLoader,
  getIsHubOrderListPopulated,
  getIsOnlineOrdersPopulated,
  getIsStoreOrdersPopulated,
  getIsOnlineOrderDetailsLoaded,
  getIsStoreOrderDetailsLoaded,
  getIsStoreOrdersLoadingFailed,
  getIsOnlineOrdersLoadingFailed,
  getIsOrdersLoadingFailed,

  getShouldFetchOnlineOrders,
  getShouldFetchOnlineActiveOrders,
  getShouldFetchStoreOrders,

  getShouldShowLoadMoreOnlineButton,
  getShouldShowLoadMoreStoreButton,

  getHubOrderList,
  getFilteredHubOrderList,
  getCombinedOrderList,
  getIsOrdersResultEmpty,

  getOnlineOrders,
  getOnlineOrderDetailsById,
  getIsOnlineKlarnaOrder,

  getTotalOrdersCount,

  getFetchOnlineIndex,
  getFetchStoreIndex,

  getIsCancelOnlineOrderLoading,
  getIsCancelOnlineOrderFailed,
  getIsCancelOnlineOrderSuccess,
  getCancelOrderWhyOptions,
  getIsOrderCancellationEnabled,

  getIsOnlineReceiptEnabled,

  getStoreOrders,
  getStoreOrderDetailsById,

  getShouldDoTracking,
  getShouldShowTrackingId,

  getSelectedTab,

  getIsStoreOrderAddressesFailed,

  getCancelAccountWhyOptions,
  getShowDeleteAccountSurvey,

  getIsProductResellEnabled,

  getOnlineOrdersDetails,
  getbackToOrderUrl,
  getcodReturnUrl,

  // Style your latest (A/B test)
  getUserHasOrderRecommendations,
  getSYLArticles,
  getRewearInfoUrl,

  getIsAddToBagLoading,
  getAddToBagVerificationItem,
  getShowAddToBagVerification,
  getAddToBagVerificationError,
  getShouldCallRateAndReviewAPI,
}
